import React, { useState } from "react";
import { X } from "lucide-react";
import { FOOTER_VALUES, VALUE_SECTIONS } from "../data/constants";

// Footer strip that summarises the platform values with iconographic
// badges. Each badge is a button that opens a MODAL with the matching
// explanation. Nothing about "our values" is rendered inline on the home
// page: this keeps the vertical rhythm clean and lets the user pull up
// the explanations only when they actually want them.
//
// Layout is vertical (icon on top, label below) so long labels never
// wrap through the neighbouring column.

function ValueModal({ value, onClose, onNavigate }) {
  if (!value) return null;
  const meta = FOOTER_VALUES.find((v) => v.anchor === value.anchor);
  const Icon = meta && typeof meta.i !== "string" ? meta.i : null;
  const idx = VALUE_SECTIONS.findIndex((v) => v.anchor === value.anchor);
  // Wrap-around navigation so the user can cycle through all 6 values from
  // either direction — no dead-end at the first/last item.
  const prev = VALUE_SECTIONS[(idx - 1 + VALUE_SECTIONS.length) % VALUE_SECTIONS.length];
  const next = VALUE_SECTIONS[(idx + 1) % VALUE_SECTIONS.length];
  return (
    <div
      className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="value-info-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-cream flex items-center justify-center"
          data-testid="value-info-close"
          aria-label="Chiudi"
        >
          <X size={16} />
        </button>
        <div className="flex items-start gap-4">
          <span
            className={`feature-badge shrink-0 !w-14 !h-14 ${meta?.color === "dark" ? "dark" : ""}`}
            aria-hidden="true"
          >
            {Icon ? <Icon size={26} strokeWidth={2} /> : <span className="text-lg">{meta?.i}</span>}
          </span>
          <div className="min-w-0 pt-1 pr-6">
            <div
              className="font-display font-semibold text-2xl text-ink leading-tight"
              data-testid="value-info-title"
            >
              {value.title}
            </div>
            <p
              className="mt-3 text-sm text-muted leading-relaxed"
              data-testid="value-info-body"
            >
              {value.body}
            </p>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between gap-2">
          <button
            onClick={() => onNavigate(prev)}
            className="text-xs font-semibold text-muted hover:text-ink"
            data-testid="value-info-prev"
          >
            ← {prev.title}
          </button>
          <div className="text-[10px] uppercase tracking-widest text-muted shrink-0">
            {idx + 1} / {VALUE_SECTIONS.length}
          </div>
          <button
            onClick={() => onNavigate(next)}
            className="text-xs font-semibold text-ink hover:text-primary text-right"
            data-testid="value-info-next"
          >
            {next.title} →
          </button>
        </div>
      </div>
    </div>
  );
}

export function ValuesFooter() {
  const [open, setOpen] = useState(null);
  return (
    <>
      <footer className="border-t border-edge bg-cream mt-6" data-testid="values-footer">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
          {FOOTER_VALUES.map((f) => {
            const Icon = typeof f.i === "string" ? null : f.i;
            const section = VALUE_SECTIONS.find((v) => v.anchor === f.anchor);
            return (
              <button
                key={f.key}
                onClick={() => section && setOpen(section)}
                className="flex flex-col items-center gap-2 min-w-0 px-2 py-2 rounded-2xl hover:bg-white transition group focus:outline-none focus-visible:ring-2 focus-visible:ring-ink"
                data-testid={`value-${f.key}`}
                title={`Scopri: ${f.label}`}
                type="button"
              >
                <span
                  className={`feature-badge shrink-0 group-hover:scale-105 transition ${f.color === "dark" ? "dark" : ""}`}
                  aria-hidden="true"
                >
                  {Icon ? <Icon size={18} strokeWidth={2} /> : f.i}
                </span>
                <span className="text-[11px] sm:text-xs font-semibold text-ink leading-tight text-center break-words min-w-0">
                  {f.label}
                </span>
              </button>
            );
          })}
        </div>
      </footer>
      <ValueModal value={open} onClose={() => setOpen(null)} onNavigate={setOpen} />
    </>
  );
}
